import PropTypes from 'prop-types';
import { Avatar, Box, Grid } from '../../../node_modules/@mui/material/index';
import CustomButton from '../../components/Button';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { getStoreLogoPreviewUrl } from './storeUtils';
import Text from '../../components/Text';
import { PLACEHOLDER_IMG_SRC } from '../../constant/constants';
import Loader from '../../components/Loader';

const LogoInput = ({ handleStoreInfo, error, handleError, store, isSaved = false }) => {
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

  // for capturing input element
  const inputRef = useRef(null);
  const isFirstRender = useRef(true);
  const [loading, setLoading] = useState(true);
  // img src for avatar component
  const [imgSrc, setImgSrc] = useState(null);

  // get URL from s3 to display
  useEffect(() => {
    // if in edit
    (async (id) => {
      const src = await getStoreLogoPreviewUrl(id);
      setImgSrc(src);
    })(store?.id);
    // stop lodaer after 10 seconds in case images are disabled in browser or similar situation
    setTimeout(() => {
      setLoading(false);
    }, 10000);
  }, []);

  // to reset image src to default placeholder image
  const resetSrc = () => {
    setImgSrc(PLACEHOLDER_IMG_SRC);
  };

  // resets src if form is saved in caseof save and add more
  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // resets value of input field that is taking store logo
    // if form is saved using save and add more (otherwise not needed)
    // with this fix now same logo can be sleected after save and add more is clicked
    handleFileReset();
    resetSrc();
  }, [isSaved]);

  // rest value of file input
  const handleFileReset = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.type = 'text';
      inputRef.current.type = 'file';
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    // check file info related errors
    if (file) {
      handleError('storeLogoURL', null);
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

      // Type
      if (!validTypes.includes(file.type)) {
        handleError('storeLogoURL', 'Only JPEG, JPG, PNG, or WebP files are allowed.');
        handleFileReset();
        setImgSrc(PLACEHOLDER_IMG_SRC);
        return;
      }

      // Size
      if (file.size > MAX_FILE_SIZE) {
        handleError('storeLogoURL', 'File size should not exceed 2 MB.');
        handleFileReset();
        setImgSrc(PLACEHOLDER_IMG_SRC);
        return;
      }
      // clearing all errors for this filed
      handleError('storeLogoURL', null);
      // to display selected file we need URL
      const newImageUrl = URL.createObjectURL(file);
      setImgSrc(newImageUrl);
      // let the main componenet handle information
      handleStoreInfo(event);
      return () => URL.revokeObjectURL(newImageUrl);
      // Process the file if needed
    }
  };

  // click input as input is hidden so another button is triggering this event
  const handleInputClick = () => {
    inputRef?.current?.click();
  };

  const handleImageLoad = () => {
    setLoading(false);
  };

  return (
    <Grid sx={{ ml: '5vh' }} alignItems="center" justifyContent="center">
      {loading && <Loader open={loading} />}
      <Avatar
        src={imgSrc}
        sx={{
          width: 280,
          height: 150,
          ml: '2px',
          backgroundColor: 'transparent',
          img: {
            objectFit: 'contain', // Ensures the full image is shown without cropping
            objectPosition: 'left'
          }
        }}
        variant="square"
      />
      <img src={imgSrc} onLoad={handleImageLoad} style={{ display: 'none' }} alt="avatar" />
      <input
        type="file"
        name={'storeLogoURL'}
        accept=".png, .jpg, .jpeg"
        onChange={handleFileChange}
        onFocus={() => handleError('storeLogoURL', null)}
        style={{ display: 'none' }}
        ref={inputRef}
      />
      {error['storeLogoURL'] && (
        <Text className="text-red-600" style={{ fontSize: 14 }}>
          {error['storeLogoURL']}
        </Text>
      )}
      <Box sx={{ display: 'flex', direction: 'row' }}>
        <CustomButton sx={{ mt: '1vh', ml: 0, width: 100 }} onClick={handleInputClick}>
          Add Logo
        </CustomButton>
        <span style={{ marginTop: '3px' }} className="text-red-600">
          *
        </span>
      </Box>
    </Grid>
  );
};

export default LogoInput;

LogoInput.propTypes = {
  handleError: PropTypes.func,
  error: PropTypes.array,
  handleStoreInfo: PropTypes.func,
  store: PropTypes.object,
  isSaved: PropTypes.bool
};
